<template>
  <v-dialog v-model="dialogVisible" max-width="1280px" scrollable persistent>
    <v-card class="hr-all-schedule-modal">
      <div class="hr-all-schedule-modal__header">
        <div class="header-left">
          <v-icon size="24" color="white">mdi-calendar-multiple</v-icon>
          <div>
            <h3>수급자 전체 일정계획</h3>
            <p>{{ selectedYear }}년 {{ selectedMonth }}월 · {{ stats.recipientCount }}명 · {{ stats.rowCount }}건</p>
          </div>
        </div>
        <div class="header-actions">
          <button
            type="button"
            class="pdf-btn"
            :disabled="isPdfLoading || stats.rowCount === 0"
            @click="downloadAllPdf"
          >
            <v-icon size="18" color="white">{{ isPdfLoading ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box' }}</v-icon>
            <span>전체 PDF</span>
          </button>
          <button type="button" class="close-btn" @click="close">
            <v-icon size="20" color="white">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <div class="hr-all-schedule-modal__toolbar">
        <div class="search-box">
          <v-icon size="16" color="#64748b">mdi-magnify</v-icon>
          <input v-model="searchText" type="text" placeholder="수급자명, 인정번호, 요양보호사 검색..." />
          <button v-if="searchText" type="button" class="search-clear" @click="searchText = ''">
            <v-icon size="12">mdi-close</v-icon>
          </button>
        </div>
        <select v-model="recipientFilter" class="recipient-filter">
          <option value="">전체 수급자</option>
          <option v-for="r in recipientOptions" :key="r.code" :value="r.code">
            {{ r.name }} ({{ r.count }}건)
          </option>
        </select>
      </div>

      <div class="hr-all-schedule-modal__body">
        <div v-if="isLoading" class="state-box">
          <v-progress-circular indeterminate color="#1e3a5f" size="40" />
          <span>일정을 불러오는 중...</span>
        </div>
        <div v-else-if="filteredRows.length === 0" class="state-box">
          <v-icon size="48" color="#cbd5e1">mdi-calendar-blank</v-icon>
          <p>해당 연월에 표시할 일정이 없습니다.</p>
          <LtcsMissingRequestCta
            :member-code="memberCode"
            :biz-gubun="bizGubun"
            :year-month="yearMonth"
            :types="SCHEDULE_LTCS_REQUEST_TYPES"
            @updated="fetchSchedules"
          />
        </div>
        <div v-else class="table-wrap">
          <table class="schedule-table">
            <thead>
              <tr>
                <th>No</th>
                <th>일자</th>
                <th>시간</th>
                <th>수급자</th>
                <th>인정번호</th>
                <th>요양보호사</th>
                <th>서비스</th>
                <th>수가</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in filteredRows" :key="row.clsp_idx">
                <td>{{ idx + 1 }}</td>
                <td>{{ formatDate(row.clsp_date) }}</td>
                <td>{{ formatTimeRange(row) }}</td>
                <td class="td-name">{{ row.clsp_recipient_name }}</td>
                <td>{{ row.clsp_recipient_cert_number || '-' }}</td>
                <td>{{ row.clsp_caregiver_name || '-' }}</td>
                <td>{{ row.clsp_service_type || '-' }}</td>
                <td class="td-fee">{{ row.clsp_fee_name || row.clsp_fee_code || '-' }}</td>
                <td class="td-action">
                  <button
                    type="button"
                    class="row-calendar-btn"
                    title="이 수급자 달력 보기"
                    @click="openRecipientCalendar(row)"
                  >
                    <v-icon size="16">mdi-calendar-clock</v-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { homeScheduleApi } from '@/services/api';
import { mapSchedulePlanRows, normalizeScheduleDate } from '@/utils/scheduleDate';
import { downloadPdfBlob, getApiErrorMessage } from '@/utils/downloadPdf';
import LtcsMissingRequestCta from '@/components/member/request/LtcsMissingRequestCta.vue';

const SCHEDULE_LTCS_REQUEST_TYPES = ['caion_ltcs_schedule_plan'] as const;

export interface AllScheduleRecipient {
  patient_code: string;
  patient_name: string;
}

interface ScheduleRow {
  clsp_idx: number;
  clsp_date: string;
  clsp_start_time?: string;
  clsp_end_time?: string;
  clsp_recipient_name: string;
  clsp_recipient_cert_number: string;
  clsp_caregiver_name?: string;
  clsp_service_type?: string;
  clsp_fee_code?: string;
  clsp_fee_name?: string;
}

interface Props {
  modelValue: boolean;
  memberCode: string;
  bizGubun?: string;
  year: number;
  month: number;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'open-recipient', recipient: AllScheduleRecipient): void;
}>();

const isLoading = ref(false);
const isPdfLoading = ref(false);
const searchText = ref('');
const recipientFilter = ref('');
const scheduleRows = ref<ScheduleRow[]>([]);

const selectedYear = computed(() => props.year);
const selectedMonth = computed(() => props.month);

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const yearMonth = computed(
  () => `${props.year}${String(props.month).padStart(2, '0')}`
);

const stats = computed(() => {
  const codes = new Set(
    scheduleRows.value
      .map((r) => (r.clsp_recipient_cert_number || '').trim())
      .filter(Boolean)
  );
  return {
    rowCount: scheduleRows.value.length,
    recipientCount: codes.size,
  };
});

const recipientOptions = computed(() => {
  const map = new Map<string, { name: string; code: string; count: number }>();
  scheduleRows.value.forEach((row) => {
    const code = (row.clsp_recipient_cert_number || '').trim();
    if (!code) return;
    const cur = map.get(code);
    if (cur) {
      cur.count += 1;
    } else {
      map.set(code, {
        code,
        name: row.clsp_recipient_name || code,
        count: 1,
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'ko'));
});

const filteredRows = computed(() => {
  let rows = scheduleRows.value;
  if (recipientFilter.value) {
    rows = rows.filter(
      (r) => (r.clsp_recipient_cert_number || '').trim() === recipientFilter.value
    );
  }
  const q = searchText.value.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => {
    const hay = [
      r.clsp_recipient_name,
      r.clsp_recipient_cert_number,
      r.clsp_caregiver_name,
      r.clsp_service_type,
      r.clsp_fee_name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
});

const formatDate = (val: unknown) => {
  const d = normalizeScheduleDate(val);
  if (!d) return '-';
  const [, m, day] = d.split('-');
  return `${parseInt(m, 10)}/${parseInt(day, 10)}`;
};

const formatTime = (time: string | undefined) => {
  if (!time) return '';
  const str = String(time);
  if (str.includes(':')) return str.substring(0, 5);
  const digits = str.replace(/\D/g, '');
  if (digits.length >= 4) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  return str;
};

const formatTimeRange = (row: ScheduleRow) => {
  const s = formatTime(row.clsp_start_time);
  const e = formatTime(row.clsp_end_time);
  if (s && e) return `${s}-${e}`;
  return s || e || '-';
};

const fetchSchedules = async () => {
  if (!props.memberCode) return;
  isLoading.value = true;
  try {
    let res = await homeScheduleApi.getAllBeneficiarySchedules(
      props.memberCode,
      yearMonth.value,
      props.bizGubun || undefined
    );
    if (
      res.data.success &&
      (!res.data.data || res.data.data.length === 0) &&
      props.bizGubun
    ) {
      res = await homeScheduleApi.getAllBeneficiarySchedules(
        props.memberCode,
        yearMonth.value,
        undefined
      );
    }
    scheduleRows.value = res.data.success ? mapSchedulePlanRows(res.data.data || []) : [];
  } catch (error: unknown) {
    const message = await getApiErrorMessage(error, '전체 일정 조회에 실패했습니다.');
    console.error('전체 일정 조회 오류:', error);
    alert(`전체 일정 조회 실패: ${message}`);
    scheduleRows.value = [];
  } finally {
    isLoading.value = false;
  }
};

const downloadAllPdf = async () => {
  if (!props.memberCode || stats.value.rowCount === 0) return;
  if (
    !confirm(
      `${selectedYear.value}년 ${selectedMonth.value}월 수급자 ${stats.value.recipientCount}명의 일정표를 PDF로 출력하시겠습니까?`
    )
  ) {
    return;
  }
  isPdfLoading.value = true;
  try {
    let res = await homeScheduleApi.downloadAllBeneficiariesPdf(
      props.memberCode,
      yearMonth.value,
      props.bizGubun || undefined
    );
    if (res.status === 404 && props.bizGubun) {
      res = await homeScheduleApi.downloadAllBeneficiariesPdf(
        props.memberCode,
        yearMonth.value,
        undefined
      );
    }
    downloadPdfBlob(res.data, `전체수급자_일정표_${yearMonth.value}.pdf`);
  } catch (error: unknown) {
    const message = await getApiErrorMessage(error, '전체 PDF 출력에 실패했습니다.');
    console.error('전체 PDF 오류:', error);
    alert(`전체 PDF 출력 실패: ${message}`);
  } finally {
    isPdfLoading.value = false;
  }
};

const openRecipientCalendar = (row: ScheduleRow) => {
  const code = (row.clsp_recipient_cert_number || '').trim();
  if (!code) return;
  emit('open-recipient', {
    patient_code: code,
    patient_name: row.clsp_recipient_name || code,
  });
};

const close = () => {
  dialogVisible.value = false;
};

watch(dialogVisible, (visible) => {
  if (visible) {
    searchText.value = '';
    recipientFilter.value = '';
    fetchSchedules();
  }
});
</script>

<style lang="scss" scoped>
.hr-all-schedule-modal {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  border-radius: 8px;
  overflow: hidden;
}

.hr-all-schedule-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: #1e3a5f;
  color: #fff;

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
    }

    p {
      margin: 4px 0 0;
      font-size: 12px;
      opacity: 0.9;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pdf-btn,
  .close-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    cursor: pointer;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  .close-btn {
    padding: 8px;
  }
}

.hr-all-schedule-modal__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;

  .search-box {
    flex: 1;
    min-width: 200px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 10px;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;

    input {
      flex: 1;
      border: none;
      outline: none;
      padding: 8px 0;
      font-size: 13px;
    }

    .search-clear {
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
    }
  }

  .recipient-filter {
    min-width: 200px;
    padding: 8px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 13px;
    background: #fff;
  }
}

.hr-all-schedule-modal__body {
  flex: 1;
  min-height: 320px;
  overflow: auto;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 20px;
  color: #64748b;
}

.table-wrap {
  padding: 0;
}

.schedule-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    text-align: left;
  }

  th {
    position: sticky;
    top: 0;
    background: #f1f5f9;
    font-weight: 600;
    color: #334155;
    z-index: 1;
  }

  tbody tr:hover {
    background: #f8fafc;
  }

  .td-name {
    font-weight: 600;
    color: #1e3a5f;
  }

  .td-fee {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .td-action {
    text-align: center;
  }

  .row-calendar-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #fff;
    color: #1e3a5f;
    cursor: pointer;

    &:hover {
      background: #eef4fb;
      border-color: #1e3a5f;
    }
  }
}
</style>
